import { deltaE00, summariseErrors } from './colour';
import { solveHeights } from './heights';
import { buildPaletteEntries, colourIds, rgbToLab } from './palette';
import { ditherMethod, quantize, type QuantizeSection } from './quantize';
import { renderMap } from './render189';
import { planSupport } from './support';
import { toneMap } from './toneMap';
import {
  FOLLOW_BUILD,
  FOLLOW_BUILD_SECTION,
  type BuildArea,
  type BuildRegion,
  type BuildResult,
  type BuildSection,
  type BuildWarning,
  type ExportMode,
  type MapartPalette,
  type MapartSettings,
  type MaterialCount,
  type SectionMap,
  type StaircaseMode,
} from './types';

export const MAP_SIZE = 128;
export const WORLD_HEIGHT = 256;

export interface BuildRequest {
  rgba: Uint8ClampedArray;
  width: number;
  length: number;
  settings: MapartSettings;
  palette: MapartPalette;
  /** Layers that dither or staircase on their own terms. Null builds everything by the settings. */
  sections: SectionMap | null;
}

/** The whole artwork, or one area per map: a map built on its own staircases from its own noobline. */
function buildAreas(width: number, length: number, mode: ExportMode): BuildArea[] {
  if (mode === 'whole') return [{ x: 0, z: 0, width, length }];
  const areas: BuildArea[] = [];
  for (let z = 0; z < length; z += MAP_SIZE) {
    for (let x = 0; x < width; x += MAP_SIZE) {
      areas.push({ x, z, width: Math.min(MAP_SIZE, width - x), length: Math.min(MAP_SIZE, length - z) });
    }
  }
  return areas;
}

type PixelArray = Int32Array | Uint8Array | Uint8ClampedArray;

/** One area of a whole-artwork per pixel array. */
export function cropArea<T extends PixelArray>(source: T, sourceWidth: number, area: BuildArea): T {
  const cropped = source.slice(0, area.width * area.length) as T;
  for (let z = 0; z < area.length; z++) {
    const start = (area.z + z) * sourceWidth + area.x;
    cropped.set(source.subarray(start, start + area.width), z * area.width);
  }
  return cropped;
}

function pasteArea<T extends PixelArray>(target: T, targetWidth: number, area: BuildArea, values: T, channels = 1) {
  const rowValues = area.width * channels;
  for (let z = 0; z < area.length; z++) {
    target.set(values.subarray(z * rowValues, (z + 1) * rowValues), ((area.z + z) * targetWidth + area.x) * channels);
  }
}

function findPaletteBlock(palette: MapartPalette, id: number, meta: number) {
  for (const colourId of colourIds(palette)) {
    const colour = palette.colors[colourId];
    for (const block of colour.blocks) {
      if (block.id === id && block.meta === meta) return { colour, block };
    }
  }
  return null;
}

export function blockDisplay(palette: MapartPalette, id: number, meta: number): string {
  return findPaletteBlock(palette, id, meta)?.block.display ?? `${id}:${meta}`;
}

/** What the block looks like in the world, or null for a block no map colour lists. */
export function blockColour(palette: MapartPalette, id: number, meta: number) {
  return findPaletteBlock(palette, id, meta)?.colour.baseRgb ?? null;
}

export function blockModel(palette: MapartPalette, id: number, meta: number) {
  return findPaletteBlock(palette, id, meta)?.block.model ?? null;
}

function resolveSection(section: BuildSection, settings: MapartSettings) {
  const followsDither = section.dither === FOLLOW_BUILD;
  const staircase: StaircaseMode =
    section.staircase === FOLLOW_BUILD ? settings.staircase : section.staircase;
  return {
    dither: ditherMethod(followsDither ? settings.dither : section.dither),
    strength: followsDither ? settings.ditherStrength : section.ditherStrength,
    staircase,
  };
}

/**
 * One palette for the whole build, so every later stage indexes the same entries. It carries all
 * three tones as soon as any section that owns a pixel staircases; flat sections are then held to
 * the normal tones through their candidate list.
 */
function planSections(request: BuildRequest) {
  const { settings, palette } = request;
  const table = request.sections?.sections ?? [FOLLOW_BUILD_SECTION];
  const owners = request.sections?.owners ?? null;
  const resolved = table.map((section) => resolveSection(section, settings));

  const owned = new Uint8Array(table.length);
  if (owners) for (let p = 0; p < owners.length; p++) owned[owners[p]] = 1;
  else owned[0] = 1;
  const paletteMode: StaircaseMode = resolved.some(
    (section, index) => owned[index] === 1 && section.staircase === 'classic',
  )
    ? 'classic'
    : 'off';

  const entries = buildPaletteEntries(palette, settings.selection, paletteMode);
  const everyTone = entries.map((_, index) => index);
  const normalTone = everyTone.filter((index) => entries[index].tone === 'normal');
  const sections: QuantizeSection[] = resolved.map((section) => ({
    dither: section.dither,
    strength: section.strength,
    candidates: section.staircase === 'classic' ? everyTone : normalTone,
  }));
  return { entries, paletteMode, sections, owners };
}

/**
 * Per pixel CIEDE2000 between the source and the map colour the server would send, so the number
 * covers shading too and not just the palette match. Opaque pixels only.
 */
function measureErrors(
  source: Uint8ClampedArray,
  rendered: Uint8ClampedArray,
  pixels: Int32Array,
): Float32Array {
  const labs = new Map<number, [number, number, number]>();
  const labOf = (r: number, g: number, b: number): [number, number, number] => {
    const key = (r << 16) | (g << 8) | b;
    let lab = labs.get(key);
    if (!lab) {
      lab = rgbToLab([r, g, b]);
      labs.set(key, lab);
    }
    return lab;
  };

  // Typed for the same reason as toneMap's lightnesses: a number[] this long throws.
  const errors = new Float32Array(pixels.length);
  let measured = 0;
  for (let p = 0; p < pixels.length; p++) {
    if (pixels[p] < 0) continue;
    const a = labOf(source[p * 4], source[p * 4 + 1], source[p * 4 + 2]);
    const b = labOf(rendered[p * 4], rendered[p * 4 + 1], rendered[p * 4 + 2]);
    errors[measured++] = deltaE00(a, b);
  }
  return errors.subarray(0, measured);
}

export function buildMapart(request: BuildRequest): BuildResult {
  const { settings, palette, rgba, width, length } = request;

  const { entries, paletteMode, sections, owners } = planSections(request);

  // Tone mapping edits pixels, so it works on a copy: the error readout has to compare the build
  // against what the user actually drew, not against the mapped version of it.
  const mapped = new Uint8ClampedArray(rgba);
  const toneReport = toneMap(mapped, entries, settings.toneMap, settings.alphaThreshold);

  const pixels = quantize({
    width,
    height: length,
    rgba: mapped,
    entries,
    metric: settings.metric,
    alphaThreshold: settings.alphaThreshold,
    serpentine: settings.serpentine,
    sections,
    owners,
  });

  const heights = new Int32Array(width * length);
  const supportCounts = new Uint8Array(width * length);
  const mapBytes = new Uint8Array(width * length);
  const previewRgba = new Uint8ClampedArray(width * length * 4);
  const regions: BuildRegion[] = [];
  let supportTotal = 0;
  let schematicBytes = 0;
  // Areas start on multiples of 128, so each area's own checkerboard parity matches the whole map's.
  for (const area of buildAreas(width, length, settings.exportMode)) {
    const areaPixels = cropArea(pixels, width, area);
    const solved = solveHeights(area.width, area.length, areaPixels, entries, paletteMode);
    const support = planSupport(area.width, area.length, areaPixels, entries, settings.support, settings.noobline);
    const rendered = renderMap(
      area.width,
      area.length,
      areaPixels,
      entries,
      solved.heights,
      solved.nooblineHeights,
      palette,
      settings.noobline,
    );
    pasteArea(heights, width, area, solved.heights);
    pasteArea(supportCounts, width, area, support.counts);
    pasteArea(mapBytes, width, area, rendered.mapBytes);
    pasteArea(previewRgba, width, area, rendered.previewRgba, 4);
    regions.push({
      ...area,
      nooblineHeights: solved.nooblineHeights,
      nooblineSupportCounts: support.nooblineCounts,
      maxY: solved.maxY,
    });
    supportTotal += support.total + (settings.noobline ? area.width : 0);
    // Both dense arrays, plus the two layers a support block can sit below the lowest pixel.
    schematicBytes = Math.max(schematicBytes, area.width * (solved.maxY + 3) * (area.length + 1) * 2);
  }
  const maxY = Math.max(...regions.map((region) => region.maxY));

  const counts = new Map<number, MaterialCount>();
  let blockCount = 0;
  let transparentCount = 0;
  const usedFlags = new Set<string>();
  const usedNames = new Set<string>();

  for (let p = 0; p < pixels.length; p++) {
    const index = pixels[p];
    if (index < 0) {
      transparentCount++;
      continue;
    }
    const block = entries[index].block;
    blockCount++;
    for (const flag of block.flags) usedFlags.add(flag);
    if (block.flags.includes('creativeOnly')) usedNames.add(block.display);
    const key = block.id * 16 + block.meta;
    const existing = counts.get(key);
    if (existing) existing.count++;
    else counts.set(key, { id: block.id, meta: block.meta, display: block.display, count: 1 });
  }

  if (supportTotal > 0) {
    const key = settings.supportBlock.id * 16 + settings.supportBlock.meta;
    const existing = counts.get(key);
    const display = blockDisplay(palette, settings.supportBlock.id, settings.supportBlock.meta);
    if (existing) existing.count += supportTotal;
    else
      counts.set(key, {
        id: settings.supportBlock.id,
        meta: settings.supportBlock.meta,
        display,
        count: supportTotal,
      });
  }

  const error = summariseErrors(measureErrors(rgba, previewRgba, pixels));

  const warnings: BuildWarning[] = [];
  if (entries.length === 0) warnings.push({ key: 'MAPART_WARN_NO_COLOURS' });
  const span = maxY + 1;
  if (span > WORLD_HEIGHT) warnings.push({ key: 'MAPART_WARN_HEIGHT_LIMIT', args: [span] });
  if (schematicBytes > 96 * 1024 * 1024)
    warnings.push({ key: 'MAPART_WARN_SCHEMATIC_SIZE', args: [Math.round(schematicBytes / (1024 * 1024))] });
  if (transparentCount > 0 && paletteMode !== 'off')
    warnings.push({ key: 'MAPART_WARN_TRANSPARENT_STAIRCASE' });
  if (usedFlags.has('decayRisk')) warnings.push({ key: 'MAPART_WARN_DECAY' });
  if (usedFlags.has('liquid')) warnings.push({ key: 'MAPART_WARN_LIQUID' });
  if ((usedFlags.has('gravity') || usedFlags.has('needsSupport')) && settings.support === 'none')
    warnings.push({ key: 'MAPART_WARN_GRAVITY_NO_SUPPORT' });
  if (usedNames.size > 0)
    warnings.push({ key: 'MAPART_WARN_CREATIVE_ONLY', args: [[...usedNames].join(', ')] });

  return {
    width,
    length,
    entries: pixels,
    paletteMode,
    regions,
    heights,
    supportCounts,
    mapBytes,
    previewRgba,
    minY: 0,
    maxY,
    blockCount,
    supportCount: supportTotal,
    error,
    tone: toneReport,
    schematicBytes,
    materials: [...counts.values()].sort((a, b) => b.count - a.count),
    warnings,
  };
}
